import {
  useCallback, useEffect, useRef, useState,
} from "react";

// Tolerance so a 1-16px sliver of overflow does not flap the cue on and off.
const OVERFLOW_THRESHOLD_PX = 16;

/**
 * Detects whether the sidebar nav has content hidden below the fold and
 * smooth-scrolls it to the bottom on demand. Re-measures on scroll and on
 * resize of the scroll container or its content; the content ResizeObserver
 * catches the async backend menu growing the list after mount.
 */
export function useNavScrollCue() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const measure = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;

    const distanceFromBottom =
      element.scrollHeight - Math.ceil(element.scrollTop) - element.clientHeight;
    setCanScrollDown(distanceFromBottom > OVERFLOW_THRESHOLD_PX);
  }, []);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    measure();
    scrollElement.addEventListener("scroll", measure, { passive: true });

    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measure);
      observer.observe(scrollElement);
      if (contentRef.current) observer.observe(contentRef.current);
    }

    return () => {
      scrollElement.removeEventListener("scroll", measure);
      observer?.disconnect();
    };
  }, [measure]);

  const scrollToBottom = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;

    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;

    element.scrollTo({
      top: element.scrollHeight,
      behavior: prefersReducedMotion ? "auto" : "smooth",
    });

    // Park focus on the scroll container so it does not stay on the pill once
    // the pill hides at the bottom (it becomes aria-hidden). preventScroll
    // keeps the smooth scroll intact; the container has tabIndex={-1}.
    element.focus({ preventScroll: true });
  }, []);

  return {
    scrollRef, contentRef, canScrollDown, scrollToBottom,
  };
}
